import React, { useEffect, useMemo, useState } from "react";
import useSummary from "../../../useSummary";
import { SLA, SLA_STATUS_NA } from "../../SLA/SLAUtils";
import {
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";
import FindingsBySeverityChart from "../FindingsBySeverity/FindingsBySeverityChart";
import TableChart, { type TableChartRowData } from "../TableChart";
import FindingsBySeverityLegend from "../FindingsBySeverity/FindingsSeverityLegend";
import {
  createColumnHelper,
  type CellContext,
  type ColumnDef,
} from "@tanstack/react-table";
import "./SLABySeverity.scss";
import useSetDataWithExport from "../useSetDataWithExport";
import useWidgetFilterNavigator from "../../../useWidgetFilterNavigator";
import useDebounceLoading from "../useDebounceLoading";
import type { SelectedScope } from "../../../../types/scope";
import type { CountBySeverity } from "../TopFeatureTables/TopFeatureTable";

// Each summary row is a tuple of [slaStatusKey, severityKey, count] as returned
// by the FINDINGS_VIEW summary aggregation (group_by sla_status + severity).
type SummaryRow = [string, string, number];

interface SLABySeverityRow extends TableChartRowData {
  sla: string;
  filterFieldValue: string;
  findings: CountBySeverity;
}

export interface SLABySeverityTableProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  id: string;
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

const SLABySeverityTable: React.FC<SLABySeverityTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const [tableData, setTableData] = useState<SLABySeverityRow[] | null>(null);
  const [maxFindings, setMaxFindings] = useState(0);

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: [`finding.sla_status`, `finding.severity`],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { setDataWithExport, setLoadedRowsCount } =
    useSetDataWithExport<SLABySeverityRow>({
      isForExport,
      index,
      setTotalWidgets,
      setData: setTableData,
      data: tableData,
      error: summaryDataError,
      loading: summaryLoading,
      setIsReadyToExport,
    });

  useEffect(() => {
    if (!!data) {
      const dataArray = (data as SummaryRow[]).reduce<SLABySeverityRow[]>(
        (acc, curr) => {
          if (curr[0] === SLA_STATUS_NA) {
            return acc;
          }

          if (!acc?.find((item) => item.sla === curr[0])) {
            const dataObj: SLABySeverityRow = {
              sla: curr[0],
              filterFieldValue: curr[0],
              findings: {
                total: curr[2],
              },
            };
            dataObj.findings[curr[1]] = curr[2];
            acc?.push(dataObj);
          } else {
            const dataObj = acc.find((item) => item.sla === curr[0]);
            if (dataObj) {
              dataObj.findings[curr[1]] = curr[2];
              dataObj.findings.total += curr[2];
            }
          }

          return acc;
        },
        []
      );
      const max = Math.max(...dataArray?.map((ds) => ds.findings?.total));
      const sortedData = dataArray.sort(
        (a, b) => b.findings.total - a.findings.total
      );

      setMaxFindings(max);
      setDataWithExport(sortedData);
    }
  }, [data]);

  const getIconClassNameBySla = (sla?: string) => {
    switch (sla) {
      case SLA.BROKEN_SLA.title:
        return "seem-icon-in-time sla-icon overdue";
      case SLA.WITHIN_SLA.title:
        return "seem-icon-in-time sla-icon in-time";
      case SLA.ALMOST_BROKEN_SLA.title:
        return "seem-icon-in-time sla-icon due-soon";

      default:
        return "";
    }
  };

  const chartClick = (severity?: string, slaStatus?: unknown) => {
    onFilterClick([
      ["in", "remediation_item.sla_status", slaStatus as string | string[]],
      ["in", "remediation_item.severity", severity as string | string[]],
    ]);
  };

  const columnHelper = createColumnHelper<SLABySeverityRow>();

  const columns = useMemo<ColumnDef<TableChartRowData, unknown>[]>(() => {
    return [
      columnHelper.accessor("sla", {
        cell: (props: CellContext<SLABySeverityRow, string>) => {
          const slaTitle = SLA[props.getValue() as keyof typeof SLA]?.title;
          return (
            <TableCell>
              <div className={"sla-status-name-wrapper"}>
                <i className={getIconClassNameBySla(slaTitle)} />
                <span className={"sla-status"}>{slaTitle}</span>
              </div>
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: "SLA Status",
        minSize: 50,
      }),
      columnHelper.accessor("findings", {
        cell: (props: CellContext<SLABySeverityRow, CountBySeverity>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div
              className={`findings-wrapper ${isForExport ? "exported" : ""}`}
            >
              <span
                className={"findings-count"}
                title={formatNumber(props.getValue()?.total) || undefined}
              >
                {formatNumber(props.getValue()?.total)}
              </span>
              <FindingsBySeverityChart
                loading={summaryLoading}
                error={summaryDataError}
                findingCounts={props.getValue()}
                namePrefix={props.row?.original?.sla}
                filterFieldValue={props.row?.original?.filterFieldValue}
                maxFindings={maxFindings}
                widgetId={id}
                onChartClick={chartClick}
                isForExport={isForExport}
                setLoaded={() => setLoadedRowsCount((prev) => prev + 1)}
              />
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Findings",
        // Original JS passes a string ("80%") to size; the shared Table consumes
        // it as-is. ColumnDef.size is typed number, so cast preserves runtime value.
        size: "80%" as unknown as number,
      }),
    ] as ColumnDef<TableChartRowData, unknown>[];
  }, [tableData]);

  const isLoading = useDebounceLoading([summaryLoading]);

  return (
    <TableChart
      className={"findings-by-sla-and-severity-table"}
      columns={columns}
      tableData={tableData}
      isLoading={isLoading}
      error={summaryDataError}
      onRefetch={refetchSummary}
      legend={<FindingsBySeverityLegend />}
      isForExport={isForExport}
    />
  );
};

export default SLABySeverityTable;
